
// Pure geometric core for "Bue fra pilhøjde" - fits a circular arc through a
// LineString range's two chord endpoints (fixed) at a given SIGNED sagitta,
// and SAMPLES that arc into a fresh polyline whose vertex count is chosen
// from the arc's own geometric size, not preserved from whatever the range
// happened to have before (see sampleArcPolyline()'s own doc comment - this
// replaced an earlier "preserve existing vertex count" design after manual
// testing showed the old design in two ways: a fixed-point-count resample
// isn't what "Bue fra pilhøjde" should mean, and its sweep-angle formula
// had a real bug - see computeArcSweep()'s own doc comment). No DOM/THREE
// dependency - plain {x,y,z} points, Level 1 (no-browser) unit testable -
// see arcFromSagitta.test.js.
//
//   Bue fra pilhøjde (geometry core)
//           |
//           +-- determineArcPlane()       - the stable local 3D arc plane
//           +-- arcRadiusFromSagitta()    - R = L²/(8s) + s/2 (task's given relation)
//           +-- computeArcSweep()         - the FIXED, continuous signed sweep angle
//           +-- computeArcSegmentCount()  - adaptive vertex-count sampling strategy
//           +-- sampleArcPolyline()       - samples the arc into a fresh point array
//
// ── The chord and the plane ─────────────────────────────────────────────
// The chord is ALWAYS the range's two (fixed) boundary vertices - only
// interior vertices are affected; every candidate arc plane MUST contain
// the chord (it is a chord of the circle), so the plane has exactly one
// free parameter: its rotation about the chord axis. determineArcPlane()
// picks that rotation from whichever signal is available:
//   1. the RANGE'S OWN existing interior vertices, if they show a genuine
//      bulge away from the straight chord (their averaged perpendicular
//      offset) - "hvis den eksisterende geometri giver et tydeligt lokalt
//      plan, skal dette kunne bruges";
//   2. otherwise a VERTICAL plane (world +Z, projected ⊥ to the chord) -
//      deterministic, independent of camera/view direction, and matches the
//      common "vertical curve" case (a sag/crest in an otherwise roughly
//      horizontal pipe/road run);
//   3. otherwise (the chord itself is ~vertical, so world-Z gives no
//      rotation signal either) world X, or Y if the chord is ~X too.
// This never depends on the viewer's camera - same input always gives the
// same plane, satisfying the task's "buens side må ikke tilfældigt afhænge
// af view direction" for the PLANE (the SIDE within that plane is the
// separate, explicit sign of `sagittaM` - see computeArcSweep()).

function clonePoint(p){ return {x: p.x, y: p.y, z: p.z}; }
function sub(a, b){ return {x: a.x - b.x, y: a.y - b.y, z: a.z - b.z}; }
function dot3(a, b){ return a.x * b.x + a.y * b.y + a.z * b.z; }
function len3(a){ return Math.hypot(a.x, a.y, a.z); }
function normalize3(a){
	const l = len3(a);
	return l > 1e-12 ? {x: a.x / l, y: a.y / l, z: a.z / l} : {x: 0, y: 0, z: 0};
}
function clamp(v, lo, hi){ return Math.min(hi, Math.max(lo, v)); }
// component of `v` perpendicular to the UNIT vector `d`
function perpOf(v, d){
	const proj = dot3(v, d);
	return {x: v.x - proj * d.x, y: v.y - proj * d.y, z: v.z - proj * d.z};
}

const PLANE_BULGE_EPS = 1e-6; // metres - below this, "the existing geometry" gives no usable rotation signal

// `chordStart`/`chordEnd`: the two (fixed) chord endpoints.
// `interiorPoints`: the range's CURRENT interior vertices (may be empty).
// Returns {d, u, source} - `d` the unit chord direction, `u` the unit
// in-plane direction PERPENDICULAR to the chord that a POSITIVE sagitta
// bulges toward (so the plane is {d, u}); `source` is "geometry" |
// "vertical-fallback" | "horizontal-fallback", for diagnostics/tests. Returns
// null only when the chord itself is degenerate (start ≈ end - no direction
// to build any plane from at all).
export function determineArcPlane(chordStart, chordEnd, interiorPoints = []){
	const chordVec = sub(chordEnd, chordStart);
	const L = len3(chordVec);
	if(L < 1e-9) return null;
	const d = {x: chordVec.x / L, y: chordVec.y / L, z: chordVec.z / L};

	if(interiorPoints && interiorPoints.length > 0){
		let bulge = {x: 0, y: 0, z: 0};
		for(const p of interiorPoints){
			const perp = perpOf(sub(p, chordStart), d);
			bulge = {x: bulge.x + perp.x, y: bulge.y + perp.y, z: bulge.z + perp.z};
		}
		bulge = {x: bulge.x / interiorPoints.length, y: bulge.y / interiorPoints.length, z: bulge.z / interiorPoints.length};
		if(len3(bulge) > PLANE_BULGE_EPS) return {d, u: normalize3(bulge), source: "geometry"};
	}

	const worldUp = {x: 0, y: 0, z: 1};
	const perpUp = perpOf(worldUp, d);
	if(len3(perpUp) > PLANE_BULGE_EPS) return {d, u: normalize3(perpUp), source: "vertical-fallback"};

	// the chord itself is (near-)vertical - world Z gave no rotation signal
	const ref = Math.abs(d.x) < 0.9 ? {x: 1, y: 0, z: 0} : {x: 0, y: 1, z: 0};
	return {d, u: normalize3(perpOf(ref, d)), source: "horizontal-fallback"};
}

// R = L²/(8·|s|) + |s|/2 - task's given relation between chord length,
// absolute pilhøjde and radius. Always positive; `sagittaM` may be signed
// (only its magnitude matters here - the SIDE is `u` vs `-u`, handled by
// computeArcSweep()). Returns null for a zero sagitta (no finite radius - a
// straight chord is the s→0 limit, handled separately).
export function arcRadiusFromSagitta(chordLengthM, sagittaM){
	const sAbs = Math.abs(sagittaM);
	if(sAbs < 1e-9) return null;
	return (chordLengthM * chordLengthM) / (8 * sAbs) + sAbs / 2;
}

// ── The signed sweep angle - root-cause fix for the "bue skifter side"
// manual-test bug ──────────────────────────────────────────────────────
//
// Chord-local 2D coordinates (origin = chord midpoint M, x-axis = d, y-axis
// = u): P0=(-L/2,0), Pn=(L/2,0), centre C=(0,k) with k = -sign(s)·(R-|s|) -
// the standard sagitta/chord/radius relation. This k formula is correct for
// BOTH the minor-arc case (|s| <= L/2, R >= s, centre on the OPPOSITE side
// from the bulge) and the major-arc case (|s| > L/2, R < s - the arc bulges
// more than a semicircle, centre on the SAME side as the bulge): both are
// the s→R∓d identity for d=sqrt(R²-(L/2)²), and the same k formula satisfies
// both algebraically (verified in this file's own derivation notes / PR
// history).
//
// THE BUG (found via manual test: pilhøjde 161cm→162cm flipped the arc to
// the opposite side, for a ~320cm chord - i.e. exactly at |s| ≈ L/2): the
// PREVIOUS implementation computed the sweep as
// atan2(cross(C→P0, C→Pn), dot(C→P0, C→Pn)) - the signed angle FROM C→P0 TO
// C→Pn via the shortest path. atan2's principal value is always in
// (-π, π], so as |s| increases smoothly through L/2 (the point where C→P0
// and C→Pn become exactly antiparallel, i.e. the true angle between them
// passes through ±180°), the COMPUTED sweep silently jumps by ∓360° - even
// though the true, continuously-growing sweep angle (now correctly a MAJOR
// arc, magnitude > 180°) never actually reversed direction. Since interior
// vertices are placed via `angle = sweep * t` for t strictly between 0 and
// 1, using the wrapped short-way value instead of the true value places
// them on a genuinely different (mirrored) path - this is the observed
// "side flip", not a rounding artefact, and no epsilon/threshold can fix a
// wrong branch choice - the FORMULA itself had to change.
//
// THE FIX: the arc's own midpoint ("peak") is always at local (0, R) for a
// positive sagitta (or (0, -R) for negative) relative to the centre - this
// holds on BOTH branches, since the peak is by definition a point on the
// circle at the sagitta's own side, and |peak - centre| = R always. Half of
// the true sweep is therefore the UNSIGNED angle between (centre->peak) and
// (centre->Pn), computed via acos of their normalised dot product - acos's
// range [0, π] can represent a half-sweep up to 180° (i.e. a FULL sweep up
// to 360°) with NO branch-cut ambiguity, because "the angle between two
// vectors" is an inherently unsigned, ≤180° quantity independent of major/
// minor. The rotation SIGN (which way to sweep) is constant for a fixed
// sign(sagitta) - derived once from the (already-correct, already-tested)
// minor-arc case - so it never flips at the boundary either. Continuity
// across |s| = L/2 (and well beyond, into major-arc territory) is verified
// in arcFromSagitta.test.js by sampling many closely-spaced sagitta values
// and asserting the computed sweep changes smoothly, never jumps.
//
// Returns {radiusM, k, sweepRad} or null for a ~zero sagitta (no finite arc
// - see arcRadiusFromSagitta()).
export function computeArcSweep(chordLengthM, sagittaM){
	const R = arcRadiusFromSagitta(chordLengthM, sagittaM);
	if(R === null) return null;

	const sign = Math.sign(sagittaM);
	const k = -sign * (R - Math.abs(sagittaM));

	// cos(halfSweep) = dot(centre->peak (unit), centre->Pn) / R, with
	// centre->peak (unit) = (0, sign) and centre->Pn = (L/2, -k)
	const c = clamp(-sign * k / R, -1, 1);
	const halfSweep = Math.acos(c);
	const sweepRad = -sign * 2 * halfSweep;

	return {radiusM: R, k, sweepRad};
}

// The curve-sampling/chord-error budget for adaptive vertex generation
// (arcs, circles - see sampleArcPolyline()/circleFromThreePoints.js's
// sampleCirclePolygon()) is DELIBERATELY a fixed internal constant, not a
// user-facing setting (task: "et internt sampling/chord-error budget
// behøver ikke nødvendigvis være en brugerindstilling. Vælg den enkleste
// klare model"). This used to be silently borrowed from
// `vectorEditing.maxSagitta` ("Maksimal pilhøjde") - an EDIT-SAFETY
// tolerance for Ret ud/Forenkle/Ortogonaliser/Fast vinkel/Parallel-
// vinkelret (how far an automated cleanup may move an EXISTING point before
// refusing), a completely different concept from "how finely should a
// brand-new parametric curve be tessellated". Reusing it for sampling meant
// a hidden global (e.g. 3 cm) could silently under-sample an explicit,
// user-typed "Pilhøjde = 80 cm" - the two numbers looked related but meant
// different things. Now fully decoupled: sampling always uses this fixed
// 2 cm budget, and `vectorEditing.maxSagitta` only ever gates the edit-
// safety operations it was originally documented for.
export const CURVE_SAMPLING_TOLERANCE_M = 0.02;

const DEFAULT_MAX_SEGMENTS = 200; // safety cap - avoids a pathological vertex count for a very tight tolerance relative to the radius

// How many straight segments are needed to keep every sub-chord within
// `toleranceM` of the TRUE circular arc - the standard "sagitta-based" arc
// flattening used throughout CAD/graphics: a segment spanning angle
// step = |sweepRad|/n has its own chord-error R·(1-cos(step/2)); solving
// R·(1-cos(step/2)) <= toleranceM for the smallest integer n. This is a
// property of the ARC ITSELF (radius + sweep), independent of how many
// vertices the range happened to have before - "den parametriske
// arc-definition er autoritativ; de genererede vertices er kun sampling af
// buen" (task §3).
export function computeArcSegmentCount(radiusM, sweepRad, toleranceM){
	const sweepAbs = Math.abs(sweepRad);
	if(!(radiusM > 0) || sweepAbs < 1e-9) return 1;

	const tol = Math.max(toleranceM, 1e-6); // guard a zero/negative tolerance rather than diverge
	const ratio = clamp(1 - tol / radiusM, -1, 1);
	const maxHalfStep = Math.acos(ratio); // largest per-segment half-angle keeping error <= tol

	if(maxHalfStep < 1e-9) return DEFAULT_MAX_SEGMENTS; // tolerance essentially unreachable for this radius - cap instead of diverging
	return clamp(Math.ceil(sweepAbs / (2 * maxHalfStep)), 1, DEFAULT_MAX_SEGMENTS);
}

// Samples the circular arc through chordStart/chordEnd at the given SIGNED
// sagitta into a FRESH point array - `n+1` vertices (n = adaptive segment
// count from computeArcSegmentCount()), chordStart/chordEnd reproduced
// exactly, every other vertex newly generated by EQUAL ARC-LENGTH shares
// (equal angle shares, since R is constant along one arc). The caller
// (VectorEditingMenu.js) splices this into the surrounding LineString and
// is responsible for reindexing any other stored arc ranges on the same
// feature (task §4) - this module has no notion of a feature or of other
// ranges.
//
// `sagittaM === 0` (or ~0): degenerate straight-chord case - just the two
// endpoints, no interior samples needed (the s→0 limit of an arc is a
// straight line - one segment is always sufficient/correct).
//
// Returns null only when the chord itself is degenerate (start ≈ end) or no
// plane could be determined. Otherwise {points, radiusM, k, segmentCount} -
// `radiusM`/`k` are null for the zero-sagitta straight case; `k` is the
// centre's local-u offset (see computeArcSweep()) - exposed so a caller can
// reconstruct the true circle centre (chordMidpoint + k·plane.u) without
// redoing this module's own math.
export function sampleArcPolyline(chordStart, chordEnd, sagittaM, plane, {toleranceM} = {}){
	if(!plane) return null;

	const chordVec = sub(chordEnd, chordStart);
	const L = len3(chordVec);
	if(L < 1e-9) return null; // degenerate chord

	if(Math.abs(sagittaM) < 1e-9){
		return {points: [clonePoint(chordStart), clonePoint(chordEnd)], radiusM: null, k: null, segmentCount: 1};
	}

	const {radiusM, k, sweepRad} = computeArcSweep(L, sagittaM);
	const n = computeArcSegmentCount(radiusM, sweepRad, toleranceM);

	const {d, u} = plane;
	const M = {x: (chordStart.x + chordEnd.x) / 2, y: (chordStart.y + chordEnd.y) / 2, z: (chordStart.z + chordEnd.z) / 2};
	const toWorld = (lx, ly) => ({x: M.x + lx * d.x + ly * u.x, y: M.y + lx * d.y + ly * u.y, z: M.z + lx * d.z + ly * u.z});
	const r0 = {x: -L / 2, y: -k}; // C -> P0, local 2D

	const points = new Array(n + 1);
	points[0] = clonePoint(chordStart);
	points[n] = clonePoint(chordEnd); // reproduced exactly - never via the general rotation below (floating-point trig noise)
	for(let i = 1; i < n; i++){
		const t = i / n;
		const angle = sweepRad * t;
		const c = Math.cos(angle), s = Math.sin(angle);
		const rx = r0.x * c - r0.y * s;
		const ry = r0.x * s + r0.y * c;
		points[i] = toWorld(rx, k + ry);
	}

	return {points, radiusM, k, segmentCount: n};
}
